import { observer } from 'mobx-react-lite'
import { useState } from 'react'
import classnames from 'classnames'
import { DatePicker } from 'shared/ui/DatePicker'
import { Dropdown } from 'shared/ui/Dropdown'
import { DropdownContent } from 'shared/ui/Dropdown/ui/DropdownContent'
import { DropdownCard } from 'shared/ui/Dropdown/ui/DropdownCard/DropdownCard'
import { EnumDropdownItemVariant } from 'shared/ui/Dropdown/ui/types'
import type { CalendarFilterStore } from 'shared/ui/CalendarDropdown/store/CalendarFilterStore'
import { Icon } from 'shared/ui/Icon'
import { CalendarFilterEnum, type ICalendarFilter } from './calendarEnum'
import styles from './styles.module.scss'

type ICalendarDropdownProps = {
  store: CalendarFilterStore
  onClose?: () => void
  triggerProps?: {
    size?: 'small' | 'medium'
    fullWidth?: boolean
  }
}

export const CalendarDropdown = observer(
  ({ store, onClose, triggerProps }: ICalendarDropdownProps) => {
    const [isDropdownOpen, setDropdownOpen] = useState(false)
    const {
      rangeTriggerValue,
      setParamsDate,
      selectedCalendarFilter,
      setSelectedCalendarFilter,
      fromFilterDate,
      toFilterDate,
      isCustomMode,
      setDateRangeValue,
      maxDate,
    } = store

    return (
      <Dropdown
        ariaLabel={'StatisticDateFilter'}
        placement={'bottom-start'}
        withTextInput
        width={'778px'}
        show={isDropdownOpen}
        triggerComponent={() => (
          <div
            className={classnames(styles.trigger, {
              [styles.medium]: triggerProps?.size === 'medium',
              [styles.fullWidth]: triggerProps?.fullWidth,
            })}
          >
            <div className={styles.trigger__icon}>
              <Icon icon={'calendar'} fontSize={16} />
            </div>
            <span className={styles.trigger__value}>{rangeTriggerValue()}</span>
            <Icon icon={'chevronDown'} fontSize={16} tertiary />
          </div>
        )}
        onClose={() => {
          setParamsDate()
          onClose?.()
        }}
        onToggle={(value) => {
          setDropdownOpen(value)
        }}
        customComponent={() => (
          <DropdownContent noStyle className={styles.contentContainer}>
            <div className={styles.optionsListWrap}>
              {Object.values(CalendarFilterEnum).map((filterValue) => (
                <DropdownCard
                  item={{
                    id: filterValue,
                    label: filterValue,
                    activeValue: selectedCalendarFilter === filterValue,
                    variant: EnumDropdownItemVariant.CheckedRight,
                  }}
                  onChange={({ label }) => {
                    if (label) {
                      setSelectedCalendarFilter(label as ICalendarFilter)
                      if (label !== CalendarFilterEnum.custom) {
                        setDropdownOpen(false)
                      }
                    }
                  }}
                  key={filterValue}
                />
              ))}
            </div>
            <DatePicker
              type={'range'}
              handleRangeDate={(from, to) => {
                if (isCustomMode) {
                  setDateRangeValue(from, to)
                }
              }}
              handleStartDate={(from) => {
                if (isCustomMode) {
                  setDateRangeValue(from, toFilterDate)
                }
              }}
              handleEndDate={(to) => {
                if (isCustomMode) {
                  setDateRangeValue(fromFilterDate, to)
                }
              }}
              startDate={fromFilterDate}
              endDate={toFilterDate}
              placeholder={'Select date'}
              hideTimePicker
              calendarClassName={styles.calendarClassName}
              className={styles.datePicker}
              minDate={!isCustomMode ? fromFilterDate : undefined}
              maxDate={!isCustomMode ? toFilterDate : maxDate}
              isDisabled={!isCustomMode}
            />
          </DropdownContent>
        )}
        PopperProps={{
          modifiers: [
            {
              name: 'offset',
              options: {
                offset: () => [0, -40],
              },
            },
          ],
        }}
      />
    )
  }
)
